import { useEffect, useRef, useState } from "react";
import { CaretDown, Trophy, MagnifyingGlass, Calendar, Play, X, Ticket } from "@phosphor-icons/react";

/**
 * Public contest landing page — reachable off-platform via QR code.
 *
 * ROUTING & AUTH
 *   - Registered as a *public* route in App.js (no <ProtectedRoute> wrapper).
 *   - Explicitly bypassed inside GateGuard (see `ALWAYS_PUBLIC` set) so the
 *     invite-only "Coming Soon" splash NEVER intercepts QR visitors.
 *
 * CONTENT PLACEHOLDERS
 *   Anything TJ hasn't finalized is rendered as a "[BRACKETED PLACEHOLDER]"
 *   inside the OFFICIAL RULES section. They can be edited by search/replace
 *   in this file — search for "// PLACEHOLDER".
 *
 * EXTERNAL LINK
 *   The "Watch on eBay Live" URL is defined once at the top of this file
 *   (EBAY_LIVE_URL). Update it when the destination is known.
 */

// The big hero headline. TJ can rewrite this monthly to hype the specific
// prize — otherwise the generic "WIN THIS!" works for any grand prize.
const HERO_HEADLINE_TOP = "Win";
const HERO_HEADLINE_BOTTOM = "This Month!";

// PLACEHOLDER — Sunday drawing date for this contest cycle. Update when a new cycle starts.
const DRAWING_DATE_LABEL = "Sunday, November 1st";
const DRAWING_TIME_LABEL = "~10:00 PM Central";

// Prize details for the current contest cycle. Update these values each
// month and the entire /contest page refreshes automatically (hero image,
// contest details table, Featured Prize section, Official Rules §9, and
// social/OG share image).
//
// To swap the photo: drop your new image into
//     /app/frontend/public/graphics/  (keep the filename below or update `imageSrc`)
// and update the surrounding copy fields.
const PRIZE = {
  imageSrc: "/graphics/needle-prize-current.png",
  imageAlt: "2026 Bowman Football Hobby Box — current Find the Needle grand prize",
  // One-line summary used in the hero copy ("... you're entered to win this <shortDesc>")
  shortDesc: "2026 Bowman Football Hobby Box",
  // Big display block on the Featured Prize section — 1 to 3 lines top-to-bottom.
  detailLines: ["2026 Bowman", "Football", "Hobby Box"],
  // Small gold pill under the detail lines — set to null to hide.
  badge: "2 Autographs per Box",
  // Optional secondary spec line (e.g. cert #) — set to null to hide.
  certLine: "Factory Sealed · NFL / NFLPA Licensed",
  // One-liner used in the Contest Details grid.
  detailsGridLine: "2026 Bowman Football · Factory-Sealed Hobby Box · 2 Autographs per box",
  // Description used verbatim in Official Rules §9. Change the retail value each month.
  rulesDescription: "One (1) grand prize: a 2026 Bowman Football factory-sealed Hobby Box (2 autograph cards per box, per manufacturer's stated odds). [PRIZE VALUE — approximate retail value $XXX USD]. Prize is awarded as-is. No cash substitution or transfer. Sponsor reserves the right to substitute a prize of equal or greater value if the advertised prize becomes unavailable.",
};

// PLACEHOLDER — full official rules content. Every "[BRACKETED VALUE]" below
// is meant to be replaced with finalized text.
const OFFICIAL_RULES = [
  {
    heading: "1. Contest name",
    body: "FIND THE NEEDLE — HUNT57 Monthly Giveaway.",
  },
  {
    heading: "2. Sponsor",
    body: "[SPONSOR INFORMATION — legal entity name, mailing address, and contact email]",
  },
  {
    heading: "3. Contest period",
    body: "The contest begins on [CONTEST START DATE] and ends on [CONTEST END DATE]. The contest runs for four (4) consecutive weeks. Each week runs from Monday 12:00 AM Central Time through Sunday 12:00 AM Central Time.",
  },
  {
    heading: "4. Eligibility",
    body: "[ELIGIBILITY / AGE / LOCATION REQUIREMENTS — e.g. Open to legal residents of the fifty (50) United States and the District of Columbia who are eighteen (18) years of age or older at the time of entry. Void where prohibited.]",
  },
  {
    heading: "5. How to enter",
    body: "Each week during the contest period, HUNT57 hides a small sewing-needle icon inside one active football listing on hunt57.com. Entrants who locate the hidden needle and click it are entered into the drawing. Maximum of one (1) entry per person per week, for a total of up to four (4) entries per person over the four-week contest period. The needle-bearing listing rotates each week; the entry window closes at Sunday 9:59 PM Central Time each week.",
  },
  {
    heading: "6. No purchase necessary / Alternate method of entry",
    body: "No purchase is necessary to enter. Finding and clicking the hidden needle icon is free and available to any Hunt57 member. To enter without an account, mail a 3\"x5\" card with your full legal name, mailing address, phone number, email address, and the words \"FIND THE NEEDLE ENTRY\" to: [SPONSOR MAILING ADDRESS]. One entry per envelope, per week. Mail-in entries must be received by Sunday 9:59 PM Central of the week they are intended for.",
  },
  {
    heading: "7. Winner selection",
    body: "Winner selection is conducted as a LIVE random spin during the HUNT57 / Breaking Legends live show, held Sunday nights at approximately 10:00 PM Central Time. All eligible entries received during the contest period are pooled into the live random spin for the featured prize. The spin is unedited and broadcast in real time.",
  },
  {
    heading: "8. Winner notification",
    body: "The selected winner is notified live during the Sunday spin and contacted by email within forty-eight (48) hours of the drawing. The winner must respond and provide a valid U.S. shipping address within seven (7) days of notification or an alternate winner may be selected.",
  },
  {
    heading: "9. Prize",
    body: PRIZE.rulesDescription,
  },
  {
    heading: "10. General conditions",
    body: "By participating, entrants agree to be bound by these Official Rules and the decisions of the Sponsor, which are final and binding in all matters. The Sponsor reserves the right to disqualify any entrant found tampering with the entry process, violating these rules, or acting in bad faith.",
  },
  {
    heading: "11. Publicity",
    body: "Except where prohibited, acceptance of a prize constitutes the winner's consent to Sponsor's use of their name, likeness, hometown, and prize information for promotional purposes without further compensation.",
  },
  {
    heading: "12. Limitation of liability",
    body: "The Sponsor is not responsible for late, lost, misdirected, or incomplete entries; technical malfunctions; or any injury or damage arising from participation or prize acceptance. This contest is in no way sponsored, endorsed, administered by, or associated with PSA, Panini America, or any of their affiliates.",
  },
  {
    heading: "13. Governing law",
    body: "[GOVERNING LAW — e.g. This contest is governed by the laws of the State of [STATE], without regard to conflict-of-laws principles.]",
  },
];

// Small building block — the four steps of "How it works".
function HowItWorksStep({ number, icon: Icon, title, body }) {
  return (
    <div className="relative flex flex-col items-start border border-white/10 bg-[#111111]/80 p-6 md:p-7 h-full">
      <span className="absolute -top-3 -left-3 bg-[#FF5F1F] text-black font-display font-black text-lg w-9 h-9 flex items-center justify-center border-2 border-[#0A0A0A]">
        {number}
      </span>
      <Icon size={28} weight="fill" className="text-[#FFD700] mb-3" />
      <h3 className="font-display font-black uppercase tracking-tight text-lg md:text-xl text-white">{title}</h3>
      <p className="mt-2 text-sm text-zinc-400 leading-relaxed">{body}</p>
    </div>
  );
}

export default function Contest() {
  const detailsRef = useRef(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [rulesOpen, setRulesOpen] = useState(false);

  // Inject page-specific meta tags for OG/social sharing. Restores originals
  // on unmount so navigating away doesn't leak contest metadata into the
  // rest of the site's pages.
  useEffect(() => {
    const prevTitle = document.title;
    const prevMeta = {};
    const set = (selector, key, value) => {
      const el = document.head.querySelector(selector);
      if (el) {
        prevMeta[selector] = { attr: key, val: el.getAttribute(key) };
        el.setAttribute(key, value);
      }
    };
    document.title = "HUNT57 Find the Needle Monthly Giveaway";
    set('meta[name="description"]', "content", "See this month's HUNT57 Find the Needle grand prize and get the official contest details, entry method, drawing schedule, and rules.");
    set('meta[property="og:title"]', "content", "FIND THE NEEDLE · WIN THE PRIZE | HUNT57");
    set('meta[property="og:description"]', "content", "Find the sewing needle hidden in one Hunt57 football listing. Click it to enter for this month's grand prize.");
    set('meta[property="og:image"]', "content", PRIZE.imageSrc);
    return () => {
      document.title = prevTitle;
      Object.entries(prevMeta).forEach(([sel, m]) => {
        const el = document.head.querySelector(sel);
        if (el && m.val != null) el.setAttribute(m.attr, m.val);
      });
    };
  }, []);

  const scrollToDetails = () => {
    detailsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="bg-[#0A0A0A] text-white" data-testid="contest-page">
      {/* -----------------------------------------------------------------
          HERO
         ----------------------------------------------------------------- */}
      <section
        data-testid="contest-hero"
        className="relative overflow-hidden border-b border-white/10"
      >
        {/* Subtle radial spotlight behind the slab */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(60% 50% at 70% 40%, rgba(255,215,0,0.18) 0%, rgba(255,95,31,0.08) 40%, transparent 75%)",
          }}
          aria-hidden="true"
        />
        <div
          className="absolute inset-0 pointer-events-none opacity-10 bg-center bg-cover"
          style={{ backgroundImage: "url('/graphics/hero-backdrop.png')" }}
          aria-hidden="true"
        />

        <div className="relative max-w-7xl mx-auto px-4 md:px-8 pt-10 md:pt-20 pb-14 md:pb-24 grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-14 items-center">
          {/* Copy column */}
          <div className="order-2 md:order-1">
            <div className="font-mono text-[10px] md:text-xs tracking-[0.35em] uppercase text-[#FF5F1F]">
              → HUNT57 · Find the Needle
            </div>
            <h1
              data-testid="contest-hero-headline"
              className="mt-3 font-display font-black uppercase leading-[0.92] tracking-tight text-white"
              style={{
                fontSize: "clamp(2.75rem, 7.5vw, 5.5rem)",
                textShadow: "0 0 40px rgba(255,215,0,0.15)",
              }}
            >
              {HERO_HEADLINE_TOP} <span className="text-[#FFD700]">{HERO_HEADLINE_BOTTOM}</span>
            </h1>
            <div className="mt-4 font-display font-black uppercase tracking-widest text-lg md:text-2xl text-white/90">
              Find the Needle
            </div>
            <p className="mt-5 text-base md:text-lg text-zinc-300 leading-relaxed max-w-xl">
              We&apos;ve hidden a tiny <span className="text-white font-semibold">sewing needle</span> inside one of the
              football listings on Hunt57. <span className="text-white font-semibold">Find it, click it,</span> and you&apos;re
              entered to win this month&apos;s <span className="text-white font-semibold">{PRIZE.shortDesc}</span>.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href="/register"
                data-testid="contest-cta-signup-hero"
                className="inline-flex items-center gap-2 bg-[#FF5F1F] hover:bg-[#FF7A3F] text-black font-display font-black uppercase tracking-widest text-sm px-6 py-3 border-2 border-[#FF5F1F] hover:border-[#FF7A3F] transition-colors"
              >
                Sign Up — It&apos;s Free
              </a>
              <button
                type="button"
                onClick={scrollToDetails}
                data-testid="contest-cta-details"
                className="inline-flex items-center gap-2 border-2 border-white/20 hover:border-white text-white font-display font-black uppercase tracking-widest text-sm px-6 py-3 transition-colors"
              >
                View Contest Details <CaretDown size={16} weight="bold" />
              </button>
            </div>
            <div className="mt-3 text-xs text-zinc-500">
              Already a member? <a href="/login" className="text-white hover:text-[#FFD700] underline" data-testid="contest-signin-link">Sign in</a> and start hunting.
            </div>
            <div className="mt-6 font-mono text-[10px] tracking-widest uppercase text-zinc-500">
              Live spin · {DRAWING_DATE_LABEL} · {DRAWING_TIME_LABEL}
            </div>
          </div>

          {/* Card visual */}
          <div className="order-1 md:order-2 relative flex justify-center">
            <button
              type="button"
              onClick={() => setLightboxOpen(true)}
              data-testid="contest-hero-card"
              aria-label={`Enlarge the ${PRIZE.shortDesc} image`}
              className="relative group focus:outline-none"
            >
              {/* Halo/glow */}
              <span
                aria-hidden="true"
                className="absolute inset-0 -m-6 blur-3xl opacity-70"
                style={{
                  background:
                    "radial-gradient(closest-side, rgba(255,215,0,0.35), rgba(255,95,31,0.15) 55%, transparent 85%)",
                }}
              />
              <img
                src={PRIZE.imageSrc}
                alt={PRIZE.imageAlt}
                className="relative w-[220px] sm:w-[260px] md:w-[320px] lg:w-[380px] max-w-full object-contain drop-shadow-[0_25px_45px_rgba(0,0,0,0.6)] transition-transform duration-500 group-hover:-translate-y-1 group-hover:scale-[1.02]"
                loading="eager"
                fetchPriority="high"
              />
              <span className="absolute bottom-3 right-3 font-mono text-[9px] tracking-widest uppercase bg-black/70 border border-white/20 text-white/80 px-2 py-1">
                Tap to enlarge
              </span>
            </button>
          </div>
        </div>
      </section>

      {/* -----------------------------------------------------------------
          CONTEST DETAILS
         ----------------------------------------------------------------- */}
      <section
        ref={detailsRef}
        data-testid="contest-details"
        className="max-w-5xl mx-auto px-4 md:px-8 py-14 md:py-20 scroll-mt-20"
      >
        <div className="font-mono text-[10px] tracking-[0.35em] uppercase text-[#FF5F1F]">→ Contest</div>
        <h2 className="mt-2 font-display font-black uppercase tracking-tight text-3xl md:text-5xl">Find the Needle</h2>
        <p className="mt-4 text-zinc-400 max-w-2xl text-base md:text-lg leading-relaxed">
          One sewing needle. Hidden in one football listing. Four weeks of hunting.
          One PSA 10 grand prize spun live on Sunday night. Here&apos;s exactly how it runs.
        </p>

        <dl className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
          {[
            ["Featured prize", PRIZE.detailsGridLine],
            ["Eligible activity", "Find the hidden sewing needle icon inside one of the football listings on Hunt57. Click the needle to lock in your entry."],
            ["Entries per week", "Maximum one (1) entry per person, per week."],
            ["Contest length", "Four (4) consecutive weeks."],
            ["Drawing", "Live spin & winner selection on the HUNT57 / Breaking Legends live show."],
            ["Scheduled time", `${DRAWING_DATE_LABEL} · around ${DRAWING_TIME_LABEL.replace("~", "")}.`],
          ].map(([label, value]) => (
            <div key={label} className="border border-white/10 bg-[#111111]/70 p-4 md:p-5">
              <dt className="font-mono text-[10px] tracking-widest uppercase text-[#FFD700]">{label}</dt>
              <dd className="mt-1 text-sm md:text-base text-zinc-200 leading-relaxed">{value}</dd>
            </div>
          ))}
        </dl>

        <p className="mt-6 text-xs text-zinc-500 leading-relaxed">
          Full eligibility, entry methods (including any alternate/no-purchase entry), start/end dates,
          winner selection procedure, and complete rules are listed in the{" "}
          <button type="button" onClick={() => setRulesOpen(true)} className="underline hover:text-white" data-testid="contest-open-rules-inline">
            Official Contest Rules
          </button>{" "}
          section at the bottom of this page.
        </p>
      </section>

      {/* -----------------------------------------------------------------
          HOW IT WORKS
         ----------------------------------------------------------------- */}
      <section
        data-testid="contest-how-it-works"
        className="border-y border-white/10 bg-[#0d0d0d]"
      >
        <div className="max-w-6xl mx-auto px-4 md:px-8 py-14 md:py-20">
          <div className="font-mono text-[10px] tracking-[0.35em] uppercase text-[#FF5F1F]">→ How it works</div>
          <h2 className="mt-2 font-display font-black uppercase tracking-tight text-3xl md:text-5xl">Four steps to the draw.</h2>

          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            <HowItWorksStep
              number="1"
              icon={MagnifyingGlass}
              title="Hunt the needle"
              body="We've hidden a tiny sewing needle icon inside one of the football listings on Hunt57. Browse the football listings and look closely."
            />
            <HowItWorksStep
              number="2"
              icon={Ticket}
              title="Click to enter"
              body="Spot the needle? Click it. That locks in your entry for the week — one entry per person, per week."
            />
            <HowItWorksStep
              number="3"
              icon={Play}
              title="Tune in Sunday night"
              body={`Join us ${DRAWING_DATE_LABEL} at ${DRAWING_TIME_LABEL} on the HUNT57 / Breaking Legends live show for the spin.`}
            />
            <HowItWorksStep
              number="4"
              icon={Trophy}
              title="Win the prize"
              body={`The selected winner receives this month's featured grand prize — the ${PRIZE.shortDesc} — subject to the official contest rules.`}
            />
          </div>
        </div>
      </section>

      {/* -----------------------------------------------------------------
          MID-PAGE CTA — sign up after the user learns the mechanic
         ----------------------------------------------------------------- */}
      <section
        data-testid="contest-mid-cta"
        className="relative overflow-hidden border-b border-white/10"
      >
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(50% 60% at 50% 50%, rgba(255,95,31,0.18) 0%, rgba(255,215,0,0.06) 40%, transparent 75%)",
          }}
          aria-hidden="true"
        />
        <div className="relative max-w-3xl mx-auto px-4 md:px-8 py-14 md:py-20 text-center">
          <div className="font-mono text-[10px] tracking-[0.35em] uppercase text-[#FFD700]">→ Ready to hunt?</div>
          <h2 className="mt-3 font-display font-black uppercase leading-tight tracking-tight text-3xl md:text-5xl text-white">
            Make a free account.<br className="hidden md:block" /> Start hunting today.
          </h2>
          <p className="mt-4 text-zinc-300 leading-relaxed max-w-xl mx-auto">
            The needle is live inside one football listing right now. You need
            a free Hunt57 account to click it and lock in your weekly entry.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <a
              href="/register"
              data-testid="contest-cta-signup-mid"
              className="inline-flex items-center gap-2 bg-[#FF5F1F] hover:bg-[#FF7A3F] text-black font-display font-black uppercase tracking-widest text-sm px-7 py-3.5 border-2 border-[#FF5F1F] hover:border-[#FF7A3F] transition-colors"
            >
              Sign Up — It&apos;s Free
            </a>
            <a
              href="/login"
              data-testid="contest-cta-signin-mid"
              className="inline-flex items-center gap-2 border-2 border-white/20 hover:border-white text-white font-display font-black uppercase tracking-widest text-sm px-7 py-3.5 transition-colors"
            >
              Sign In
            </a>
          </div>
        </div>
      </section>

      {/* -----------------------------------------------------------------
          FEATURED PRIZE
         ----------------------------------------------------------------- */}
      <section
        data-testid="contest-featured-prize"
        className="max-w-6xl mx-auto px-4 md:px-8 py-14 md:py-20 grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-14 items-center"
      >
        <div className="flex justify-center order-2 md:order-1">
          <button
            type="button"
            onClick={() => setLightboxOpen(true)}
            data-testid="contest-featured-card"
            aria-label={`Enlarge the ${PRIZE.shortDesc} image`}
            className="relative group focus:outline-none"
          >
            <span
              aria-hidden="true"
              className="absolute inset-0 -m-4 blur-2xl opacity-60"
              style={{
                background:
                  "radial-gradient(closest-side, rgba(255,215,0,0.3), rgba(255,95,31,0.12) 60%, transparent 90%)",
              }}
            />
            <img
              src={PRIZE.imageSrc}
              alt={PRIZE.imageAlt}
              className="relative w-[220px] sm:w-[260px] md:w-[320px] object-contain drop-shadow-[0_25px_45px_rgba(0,0,0,0.6)] transition-transform duration-500 group-hover:-translate-y-1"
              loading="lazy"
            />
          </button>
        </div>

        <div className="order-1 md:order-2">
          <div className="font-mono text-[10px] tracking-[0.35em] uppercase text-[#FFD700]">→ Featured Prize</div>
          {PRIZE.detailLines[0] && (
            <h2 className="mt-2 font-display font-black uppercase leading-tight tracking-tight text-3xl md:text-5xl">
              {PRIZE.detailLines[0]}
            </h2>
          )}
          {PRIZE.detailLines[1] && (
            <div className="mt-1 font-display font-black uppercase leading-tight tracking-tight text-2xl md:text-4xl text-[#FF5F1F]">
              {PRIZE.detailLines[1]}
            </div>
          )}
          {PRIZE.detailLines[2] && (
            <div className="mt-1 font-display font-black uppercase leading-tight tracking-tight text-xl md:text-3xl text-white/90">
              {PRIZE.detailLines[2]}
            </div>
          )}
          {PRIZE.badge && (
            <div className="mt-4 inline-flex items-center gap-2 border-2 border-[#FFD700] bg-[#FFD700]/10 text-[#FFD700] font-display font-black uppercase tracking-widest text-xs md:text-sm px-3 py-1.5">
              {PRIZE.badge}
            </div>
          )}
          {PRIZE.certLine && (
            <div className="mt-3 font-mono text-xs tracking-widest uppercase text-zinc-500">
              {PRIZE.certLine}
            </div>
          )}
          <p className="mt-6 text-zinc-400 leading-relaxed max-w-md">
            This is the current grand prize going to one lucky HUNT57 hunter
            at the end of the four-week Find the Needle run.
          </p>
        </div>
      </section>

      {/* -----------------------------------------------------------------
          LIVE DRAWING
         ----------------------------------------------------------------- */}
      <section
        data-testid="contest-live-drawing"
        className="border-y border-white/10 bg-gradient-to-b from-[#0d0d0d] to-[#0A0A0A]"
      >
        <div className="max-w-4xl mx-auto px-4 md:px-8 py-14 md:py-20 text-center">
          <Calendar size={40} weight="fill" className="mx-auto text-[#FF5F1F]" />
          <h2 className="mt-4 font-display font-black uppercase leading-tight tracking-tight text-3xl md:text-5xl">
            Live spin. Live winner.
          </h2>
          <p className="mt-5 text-zinc-300 leading-relaxed max-w-2xl mx-auto">
            Winner selection is a live spin on the HUNT57 / Breaking Legends
            live show — no black-box RNG, no random emails. Tune in and watch
            the wheel decide.
          </p>
          <div className="mt-8 flex flex-col items-center gap-2">
            <div className="font-display font-black uppercase tracking-tight text-4xl md:text-6xl text-white">
              {DRAWING_DATE_LABEL}
            </div>
            <div className="font-display font-black uppercase tracking-widest text-xl md:text-3xl text-[#FFD700]">
              {DRAWING_TIME_LABEL}
            </div>
          </div>
          <div className="mt-10 font-mono text-[10px] tracking-widest uppercase text-zinc-500">
            Stream link posted on Hunt57 and our socials right before showtime.
          </div>
        </div>
      </section>

      {/* -----------------------------------------------------------------
          OFFICIAL RULES (accordion)
         ----------------------------------------------------------------- */}
      <section
        data-testid="contest-official-rules"
        className="max-w-4xl mx-auto px-4 md:px-8 py-14 md:py-20"
      >
        <div className="border border-white/10 bg-[#111111]/70">
          <button
            type="button"
            onClick={() => setRulesOpen((v) => !v)}
            data-testid="contest-rules-toggle"
            aria-expanded={rulesOpen}
            className="w-full flex items-center justify-between gap-4 p-5 md:p-6 text-left"
          >
            <div>
              <div className="font-mono text-[10px] tracking-[0.35em] uppercase text-[#FF5F1F]">→ Legal</div>
              <div className="mt-1 font-display font-black uppercase tracking-tight text-2xl md:text-3xl">
                Official Contest Rules
              </div>
              <div className="mt-1 text-xs text-zinc-500 uppercase tracking-widest font-mono">
                {rulesOpen ? "Tap to collapse" : "Tap to expand — full rules and eligibility"}
              </div>
            </div>
            <CaretDown
              size={22}
              weight="bold"
              className={`text-zinc-400 shrink-0 transition-transform ${rulesOpen ? "rotate-180" : ""}`}
            />
          </button>

          {rulesOpen && (
            <div className="border-t border-white/10 p-5 md:p-8 space-y-5 text-sm leading-relaxed text-zinc-300" data-testid="contest-rules-body">
              <div className="border border-[#FFD700]/40 bg-[#FFD700]/5 text-[#FFD700] font-mono uppercase tracking-widest text-[11px] p-3">
                ⚠ Draft — Some sections below contain [BRACKETED PLACEHOLDERS] pending final legal review.
                Do not treat any bracketed text as final Official Rules.
              </div>
              {OFFICIAL_RULES.map((r) => (
                <div key={r.heading}>
                  <div className="font-display font-black uppercase text-white text-sm tracking-wide">{r.heading}</div>
                  <p className="mt-1 text-zinc-400 whitespace-pre-wrap">{r.body}</p>
                </div>
              ))}
              <div className="pt-4 border-t border-white/10 font-mono text-[10px] tracking-widest uppercase text-zinc-500">
                Contest is in no way sponsored, endorsed, or administered by PSA or Panini.
              </div>
            </div>
          )}
        </div>
      </section>

      {/* -----------------------------------------------------------------
          LIGHTBOX
         ----------------------------------------------------------------- */}
      {lightboxOpen && (
        <div
          className="fixed inset-0 z-[80] bg-black/95 flex items-center justify-center p-4 cursor-zoom-out"
          onClick={() => setLightboxOpen(false)}
          data-testid="contest-lightbox"
        >
          <button
            type="button"
            onClick={() => setLightboxOpen(false)}
            aria-label="Close enlarged card"
            data-testid="contest-lightbox-close"
            className="absolute top-4 right-4 md:top-6 md:right-6 text-white/70 hover:text-white p-2"
          >
            <X size={28} weight="bold" />
          </button>
          <img
            src={PRIZE.imageSrc}
            alt={PRIZE.imageAlt}
            className="max-w-full max-h-[92vh] object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}
